import React, { useEffect, useRef, useState } from "react";
import "./AiVoiceanimation.css";
import { useSelector } from "react-redux";
import LoaderComponent from "../LoaderComponent/LoaderComponent";
import { CircularProgress } from "@mui/material";

function AiVoiceanimation({ togglePlay }) {
  const texttospeech = useSelector((state) => state.texttospeech);

  const audioRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);

  const audioUrl = texttospeech?.voice?.audio_url;

  /*
   * The player is only shown once the response has landed AND the browser
   * has buffered enough data to start playing. Anything short of that is a
   * loading state, so the two conditions are merged into one flag.
   */
  const canShowPlayer = texttospeech.success && audioUrl && isReady;

  /*
   * Reset the audio state whenever a new audio URL arrives.
   */
  useEffect(() => {
    setIsReady(false);
    setIsPlaying(false);
    setHasError(false);

    const audio = audioRef.current;

    if (!audio || !audioUrl) {
      return;
    }

    /*
     * Explicitly start loading the new audio URL immediately.
     */
    audio.load();

    /*
     * If the browser already has enough data available,
     * mark it as ready immediately.
     */
    if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      setIsReady(true);
    }
  }, [audioUrl]);

  /*
   * Browser has enough data to start playback.
   */
  const handleCanPlay = () => {
    const audio = audioRef.current;

    if (audio && audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {

      setIsReady(true);
      setHasError(false);
    }
  };

  /*
   * Audio playback started.
   */
  const handlePlay = () => {
    setIsPlaying(true);
  };

  /*
   * Audio paused.
   */
  const handlePause = () => {
    setIsPlaying(false);
  };

  /*
   * Audio finished playing.
   */
  const handleEnded = () => {
    setIsPlaying(false);
  };

  /*
   * Audio loading/playback error.
   */
  const handleError = (event) => {

    setIsReady(false);
    setIsPlaying(false);
    setHasError(true);
  };

  /*
   * Audio has started loading data.
   */
  const handleLoadStart = () => {

    setIsReady(false);
    setHasError(false);
  };

  /*
   * Useful for monitoring buffering.
   */
  const handleProgress = () => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    const buffered = audio.buffered.length > 0 ? audio.buffered.end(0) : 0;

    console.log("Audio buffering:", {
      readyState: audio.readyState,
      buffered,
      duration: audio.duration,
    });

    /*
     * Additional safety check:
     * if enough data is available, consider the audio ready.
     */
    if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      setIsReady(true);
    }
  };

  return (
    <div className="Ai-Voice-animation-wrapper">
      <div className="Ai-Voice-animation-container">
        <div className="Ai-Voice-animation-play-progress-folder">
          <div className="audio-wrapper">
            {/*
             * Single loading state: covers both "response not back
             * yet" and "response back but still buffering".
             */}
            {!canShowPlayer && !hasError && (
              //    <LoaderComponent />
              <span className="flex items-center gap-[5px]">
                <CircularProgress size={15} />
              </span>
            )}

            {/*
             * Show error if the audio couldn't be loaded.
             */}
            {hasError && (
              <div className="audio-error">Unable to load audio.</div>
            )}

            {/*
             * Mounted as soon as the URL exists so the download can
             * start, but hidden until it is actually playable.
             * Hidden via CSS rather than unmounted - unmounting
             * would throw away the buffer and never let isReady
             * flip to true.
             */}
            {texttospeech.success && audioUrl && (
              <audio
                ref={audioRef}
                controls
                preload="auto"
                src={audioUrl}
                style={{ display: canShowPlayer ? "block" : "none" }}
                onLoadStart={handleLoadStart}
                onCanPlay={handleCanPlay}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handleEnded}
                onError={handleError}
                onProgress={handleProgress}
              />
            )}
          </div>
        </div>

        <i
          className="fa-solid fa-x Ai-Voice-animation-close-btn"
          onClick={togglePlay}
        />
      </div>
    </div>
  );
}

export default AiVoiceanimation;
